import { allPieces, images } from "@/utils/data"
import classNames from "classnames"

import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { hideChatCloud } from "@/store/globalSlice"
import PlayerTabProperties from "./PlayerTabProperties"
import { mapDesigns } from "@mono/mapDesigns"
import React, { useEffect, useRef } from "react"
import PlayerListItemInfo from "./PlayerListItemInfo"
import Money from "@/Components/Money"
import PlayerTabProperties2 from "./PlayerTabProperties2"
import PlayerListItemInfo2 from "./PlayerListItemInfo2"
import ChatCloud from "@/Components/ChatCloud"
import activeTurn from "@/images/your-turn-badge.png"
import { getUserCreds } from "@/utils/utils"
interface PlayerListItemProps {
  player: IPlayer
  user: IUser
}
const tempUserImage =
  "https://s3-alpha-sig.figma.com/img/9917/e134/26c6ed58b51b7a188990451cddea88fb?Expires=1729468800&Key-Pair-Id=APKAQ4GOSFWCVNEHN3O4&Signature=LoGvo5YfDVISZCeL2W0Kq6R3r4rQJCAAKzUIvz4UQdJ2fo9iBXtyneg1XryAcNNZERF9mODZieBD4EhJDmRRQdfevSCpZ9v8ZFOBup5Me7VXP0tudPMcvmVjo2Ooz1APfOZ~0pM-QuRpkndnPSLN~Z6g0Y1wGa9UTRr5H1p8R1Sh017S6J0HS5Kmag234Pl91w60cGaMKE2fl1SugTADRjlOGgv2WRueQy1xor2VTVO3LxuzpAJ5bdRj9a4b7L66Ffl67TR8wICLWM7ZSJ4JmqcUoZL0vJ6e1d3hmm8b5trrGLiyskZChm7AcUlhdB8A3CclkxB1KJmZiurZCx4qlg__"

const PlayerListItem2: React.FC<PlayerListItemProps> = ({ player, user }) => {
  // const piece = allPieces.find((item) => item.id === player.pieceId)
  if (!player) return null
  const wrapperRef = useRef(null)
  const dispatch = useAppDispatch()
  const chatClouds = useAppSelector((state) => state.global.chatClouds)
  const mapId = useAppSelector((state) => state.global.myGame?.settings.mapId)
  const map = mapDesigns.find((map) => map.id === mapId)
  const properties = player.properties.map(
    (propertyId) =>
      map?.cells.find((cell) => cell.order === propertyId) as ICell
  )
  const situation = useAppSelector((state) => state.global.myGame?.situation)
  // Seconds until this player's seat goes to a bot, counted down by the server
  // while they are dropped. Everyone at the table sees it: a seat that stops
  // playing otherwise reads as a game that has hung, when it is really a game
  // that is one minute from carrying on without them. The player's own state is
  // the second gate, so a countdown whose closing message got lost can never
  // leave a badge on a seat that is back in play.
  const botTakeoverSecondsLeft = useAppSelector(
    (state) => state.global.botTakeoverCountdowns[player.order]
  )
  const showBotTakeover =
    botTakeoverSecondsLeft != null && !!player.disconnected && !player.bot
  // Nobody is "on turn" once the game is decided — the winner's card would
  // otherwise keep glowing behind the game over screen.
  const isActiveTurn =
    situation?.turn === player.id && !player.isBankrupt && !situation?.gameOver

  // Get chat cloud data for this player
  const chatCloudData = chatClouds[player.order]

  // Our own seat always sits in the same spot (bottom of the left sidebar, see
  // utils/seating.ts), which a couple of details below have to know about.
  const isMyCard = player.id === getUserCreds().email

  return (
    <div className="relative  w-[98%]">
      <img
        src={activeTurn}
        className={classNames(
          "h-24 absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[72%] scale-0 transition-all duration-300 ease-in-out",
          {
            "scale-100": isActiveTurn,
          }
        )}
      />
      <div
        className={classNames(
          // minmax(0,1fr), not 1fr/auto: a grid track's automatic minimum is
          // its content's min-content width, so a long player name used to push
          // this whole card wider than the sidebar column it sits in. Flooring
          // the track at 0 keeps the card at the column's width and lets the
          // name inside truncate instead (see PlayerListItemInfo2).
          "grid grid-cols-[minmax(0,1fr)] gap-x-2 rounded-lg transition-all duration-300",
          {
            "scale-100 ring-2 ring-[rgba(173,232,8,0.9)]": isActiveTurn,
          }
        )}
        id={`player-list-item-${player.order}`}
        style={{
          backgroundImage: `url(${images.playerItemBg})`,
          // background: "black",
          boxShadow: isActiveTurn
            ? "inset 0 0 0 1px rgba(205, 248, 120, 0.9), 0 0 12px rgba(0, 0, 0, 0.95), 0 0 14px rgba(189, 240, 60, 0.95), 0 0 28px rgba(173, 232, 8, 0.8), 0 0 42px rgba(173, 232, 8, 0.7), 0 0 64px rgba(132, 184, 6, 0.55), 0 0 92px rgba(101, 141, 4, 0.4)"
            : "0px 0px 10px 0px rgba(0, 0, 0, 1)",
        }}
        ref={wrapperRef}
        // style={{boxShadow:""}}
      >
        {/* Chat Cloud */}
        {chatCloudData && (
          <ChatCloud
            message={chatCloudData.message}
            username={chatCloudData.username}
            isVisible={chatCloudData.isVisible}
            playerOrder={player.order}
            // Our own card is pinned to the bottom of the left sidebar, so its
            // bubble has to open upwards to stay clear of the trade button.
            placement={isMyCard ? "above" : "below"}
            onHide={() => {
              dispatch(hideChatCloud({ playerOrder: player.order }))
            }}
          />
        )}

        <PlayerListItemInfo2 player={player} user={user}></PlayerListItemInfo2>

        {/* A row of the card rather than something floating over it: the
            properties strip below is already busy, and a badge overlapping it
            would hide the very holdings the rest of the table is weighing up
            while they decide whether to wait. Turns red for the last stretch. */}
        {showBotTakeover && (
          <div
            role="status"
            data-testid={`game.bot-takeover-countdown-${player.order}`}
            title="This player lost connection. If they don't get back in time a bot takes over their seat, so the game carries on either way."
            className={classNames(
              "bot-takeover-strip flex items-center justify-center gap-2.5 border-y-2 px-3 py-2 font-poppins leading-none text-white",
              botTakeoverSecondsLeft <= 10
                ? "bot-takeover-strip-urgent bg-[#8f1d1d] border-[#ff8a6b]"
                : "bg-[#8a4c0a] border-[#f0b45c]"
            )}
          >
            <i className="fa-solid fa-plug-circle-xmark text-xl sm:text-2xl"></i>
            <div className="flex flex-col items-start gap-1">
              <span className="text-[0.65rem] sm:text-xs font-bold uppercase tracking-widest">
                Disconnected
              </span>
              <span className="flex items-baseline gap-1.5 text-sm sm:text-base font-semibold">
                Bot takes over in
                <span className="text-xl sm:text-2xl font-bold tabular-nums">
                  {botTakeoverSecondsLeft}s
                </span>
              </span>
            </div>
          </div>
        )}

        {/* <div className="hidden md:block"> */}
        <PlayerTabProperties2
          properties={properties}
          playerOrder={player.order}
        ></PlayerTabProperties2>
        {/* </div> */}
      </div>
    </div>
  )
}

export default React.memo(PlayerListItem2)
